"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "./LanguageProvider";

type Service = {
  title: { sv: string; en: string };
  text: { sv: string; en: string };
  cta: { sv: string; en: string };
  href: string;
  /** Stills from Bilspektrum's own drone films (public/assets/images/bilspektrum). */
  image: string;
  imagePosition?: string;
};

const SERVICES: Service[] = [
  {
    title: { sv: "Nya bilar", en: "New cars" },
    text: {
      sv: "Auktoriserad återförsäljare av Mazda och Hyundai. Se aktuella kampanjer och boka provkörning.",
      en: "Authorised Mazda and Hyundai dealer. See current offers and book a test drive.",
    },
    cta: { sv: "Se kampanjer", en: "View offers" },
    href: "/kampanjer",
    image: "/assets/images/bilspektrum/showroom-podium.jpg",
    imagePosition: "60% 50%",
  },
  {
    title: { sv: "Bilar i lager", en: "Cars in stock" },
    text: {
      sv: "Noggrant utvalda nya och begagnade bilar, servade och redo för provkörning.",
      en: "Carefully selected new and used cars, serviced and ready for a test drive.",
    },
    cta: { sv: "Se bilar i lager", en: "See cars in stock" },
    href: "/fordon-i-lager",
    image: "/assets/images/bilspektrum/showroom-stock.jpg",
  },
  {
    title: { sv: "Verkstad & service", en: "Workshop & service" },
    text: {
      sv: "Service och reparationer på alla bilmärken, med särskild kompetens på Mazda och Hyundai.",
      en: "Service and repairs on all brands, with special expertise in Mazda and Hyundai.",
    },
    cta: { sv: "Boka service", en: "Book service" },
    href: "/verkstad#boka",
    image: "/assets/images/bilspektrum/workshop.jpg",
    imagePosition: "70% 50%",
  },
  {
    title: { sv: "Om Bilspektrum", en: "About Bilspektrum" },
    text: {
      sv: "Familjeföretag i Umeå sedan 1973. Möt teamet och läs vår historia.",
      en: "Family business in Umeå since 1973. Meet the team and read our story.",
    },
    cta: { sv: "Om oss", en: "About us" },
    href: "/om-oss",
    image: "/assets/images/bilspektrum/showroom-entre.jpg",
    imagePosition: "50% 50%",
  },
];

const EASE = [0.22, 1, 0.36, 1] as const;

export const ServicesSection = () => {
  const { language } = useLanguage();
  const sv = language === "sv";
  const reduceMotion = useReducedMotion();
  const [active, setActive] = useState(0);

  return (
    <section id="tjanster" className="bg-neutral-950 py-20 text-white lg:py-28">
      <div className="container mx-auto max-w-6xl px-6">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-6 lg:mb-14">
          <div className="max-w-2xl">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: EASE }}
              className="mb-4 text-xs font-medium uppercase tracking-[0.25em] text-white/60"
            >
              {sv ? "Allt under ett tak" : "Everything under one roof"}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
              className="font-display text-4xl font-bold lg:text-5xl"
            >
              {sv ? "Våra tjänster" : "Our services"}
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="max-w-sm text-white/65"
          >
            {sv
              ? "Från första provturen till servicen tio år senare, på samma adress, med samma människor."
              : "From the first test drive to the service ten years later, at the same address, with the same people."}
          </motion.p>
        </div>

        {/* Desktop: expanding panels. Phones: stacked cards. */}
        {/* The trigger sits on the (unclipped) container: a fully clipped element
            never counts as "in view" for IntersectionObserver. */}
        <motion.div
          initial={reduceMotion ? false : "hidden"}
          whileInView="shown"
          viewport={{ once: true, amount: 0.15 }}
          className="flex flex-col gap-3 lg:h-[34rem] lg:flex-row"
        >
          {SERVICES.map((service, index) => {
            const isActive = active === index;
            return (
              <motion.div
                key={service.href}
                variants={{
                  hidden: { clipPath: "inset(100% 0% 0% 0%)" },
                  shown: { clipPath: "inset(0% 0% 0% 0%)", transition: { duration: 1, delay: index * 0.12, ease: EASE } },
                }}
                onMouseEnter={() => setActive(index)}
                onFocus={() => setActive(index)}
                className={`relative h-72 overflow-hidden rounded-lg transition-[flex-grow] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] sm:h-80 lg:h-auto lg:min-w-0 ${
                  isActive ? "lg:flex-[3]" : "lg:flex-1"
                }`}
              >
                <Link href={service.href} className="group absolute inset-0 block outline-none">
                  <Image
                    src={service.image}
                    alt=""
                    fill
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    style={{ objectPosition: service.imagePosition ?? "50% 50%" }}
                    className={`object-cover transition-transform duration-[1.6s] ease-out ${
                      isActive ? "lg:scale-105" : "lg:scale-100"
                    }`}
                  />
                  <div
                    aria-hidden
                    className={`absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10 transition-opacity duration-700 ${
                      isActive ? "lg:opacity-100" : "lg:opacity-80"
                    }`}
                  />

                  {/* Collapsed (desktop): vertical title */}
                  <span
                    aria-hidden
                    className={`absolute bottom-6 left-1/2 hidden -translate-x-1/2 rotate-180 whitespace-nowrap font-display text-lg font-bold transition-opacity duration-300 [writing-mode:vertical-rl] lg:block ${
                      isActive ? "opacity-0" : "opacity-100 delay-300"
                    }`}
                  >
                    {service.title[language]}
                  </span>

                  {/* Expanded content (always visible on phones) */}
                  <div
                    className={`absolute inset-x-0 bottom-0 p-6 transition-all duration-500 lg:p-8 ${
                      isActive
                        ? "lg:translate-y-0 lg:opacity-100 lg:delay-300"
                        : "lg:pointer-events-none lg:translate-y-4 lg:opacity-0"
                    }`}
                  >
                    <span className="mb-2 block text-xs font-medium uppercase tracking-[0.2em] text-white/60">
                      0{index + 1}
                    </span>
                    <h3 className="mb-2 font-display text-2xl font-bold lg:text-3xl">{service.title[language]}</h3>
                    <p className="mb-5 max-w-md text-sm leading-relaxed text-white/80 lg:text-base">
                      {service.text[language]}
                    </p>
                    <span className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-neutral-900 transition group-hover:gap-3 group-focus-visible:ring-2 group-focus-visible:ring-white">
                      {service.cta[language]}
                      <ArrowRight className="h-4 w-4" aria-hidden />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
